import { useEffect } from "react";
import { useParams } from "react-router-dom";

import {
  Box,
  Container,
  Table,
  TableBody,
  TableContainer,
} from "@mui/material";
import ApprovalOutlinedIcon from "@mui/icons-material/ApprovalOutlined";

import ErrorChip from "components/utils/ErrorChip";
import SectionHeader from "components/utils/SectionHeader";
import ProtectedRoute from "components/auth/ProtectedRoute";
import LinearIndeterminate from "components/utils/LinearLoader";
import { usePavmReject, usePavmApprove } from "hooks/pavm/requests";
import { RequestSchema, useUserServiceRequestDetail } from "hooks/user/base";
import {
  ClosedTableHeader,
  ClosedRow,
  PendingTableHeader,
  PendingRow,
} from "./Rows";

export const RequestWindow = ({ request }: { request: RequestSchema }) => {
  const { id, status } = request;
  const reject = usePavmReject();
  const approve = usePavmApprove();

  return (
    <Container maxWidth="xl" sx={{ boxShadow: 4, mb: 2 }}>
      <TableContainer>
        <Table aria-label="collapsible table">
          {status === "PENDING" ? (
            <PendingTableHeader />
          ) : (
            <ClosedTableHeader />
          )}

          <TableBody>
            {status === "PENDING" ? (
              <PendingRow
                key={id}
                reject={reject}
                approve={approve}
                request={request}
                defaultOpen={true}
              />
            ) : (
              <ClosedRow key={id} request={request} defaultOpen={true} />
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Container>
  );
};

export const RequestDetail = () => {
  const { id = "" } = useParams();

  const request = useUserServiceRequestDetail({ id });
  const data = request.data?.data;

  const title = `Request: ${data?.subject}`;

  useEffect(() => {
    document.title = "GRACE | requests";
  }, []); // run once on mount

  return (
    <ProtectedRoute>
      {request.isLoading ? (
        <LinearIndeterminate />
      ) : request.isError ? (
        <Box
          sx={{
            height: "88vh", // full height to center vertically (within the viewport)
            display: "flex", // use flexbox layout
            alignItems: "center", // center vertically
            justifyContent: "center", // center horizontally
          }}
        >
          <ErrorChip label={request.error.response?.data?.message} />
        </Box>
      ) : (
        <Container maxWidth="xl">
          <SectionHeader title={title} icon={<ApprovalOutlinedIcon />} />

          {!!data && <RequestWindow request={data} />}
        </Container>
      )}
    </ProtectedRoute>
  );
};
